import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import LivePreviewPanel from '../components/LivePreviewPanel';
import { useDebounce } from '../hooks/useDebounce';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  Save, 
  ArrowLeft, 
  Palette, 
  Type, 
  Layout, 
  Image, 
  Settings,
  Upload,
  Printer
} from 'lucide-react';
import { useEventRoute } from '../utils/eventRef';

const BadgeConfigurationPage = () => {
  const { eventId } = useEventRoute();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [badgeConfig, setBadgeConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('front'); // 'front', 'back', 'global', 'print'
  
  // Debounced badge config for live preview (500ms delay)
  const debouncedBadgeConfig = useDebounce(badgeConfig, 500);

  // Default badge configuration
  const defaultBadgeConfig = {
    badgeWidth: 400,
    badgeHeight: 600,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 2,
    backgroundColor: '#ffffff',
    globalSettings: {
      fontFamily: 'Arial, sans-serif',
      primaryColor: '#1e293b',
      secondaryColor: '#64748b',
      logoUrl: null,
      logoWidth: 80,
      logoHeight: 80,
      logoX: 160,
      logoY: 20
    },
    printConfig: {
      paperSize: 'A4',
      orientation: 'portrait',
      margin: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20
      },
      scale: 1.0,
      quality: 'high',
      colorMode: 'color',
      doubleSided: false,
      badgesPerPage: 1,
      cutLines: false,
      backgroundPrint: true
    },
    front: {
      fields: [
        {
          key: 'name',
          label: 'Name',
          visible: true,
          x: 50,
          y: 150,
          style: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1e293b',
            fontFamily: 'inherit'
          }
        },
        {
          key: 'company',
          label: 'Company',
          visible: true,
          x: 50,
          y: 200,
          style: {
            fontSize: '16px',
            fontWeight: 'normal',
            color: '#64748b',
            fontFamily: 'inherit'
          }
        }
      ]
    },
    back: {
      fields: [
        {
          key: 'qr_code',
          label: 'QR Code',
          visible: true,
          x: 150,
          y: 250,
          style: {
            width: '100px',
            height: '100px'
          }
        }
      ]
    }
  };

  // Fetch badge configuration
  const { data: config, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['badgeConfig', eventId],
    queryFn: () => ApiService.getBadgeConfig(eventId),
    enabled: !!eventId,
    retry: 1,
  });

  // Update badge configuration mutation (for old API compatibility)
  const updateConfigMutation = useMutation({
    mutationFn: (configData) => ApiService.updateBadgeConfig(eventId, configData),
    onSuccess: () => {
      queryClient.invalidateQueries(['badgeConfig', eventId]);
      showSuccess('Badge configuration updated successfully!');
      setIsEditing(false);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update badge configuration. Please try again.';
      showError(errorMessage);
      console.error('Update config error:', error);
    },
  });

  // Save badge configuration mutation (new API)
  const saveConfigMutation = useMutation({
    mutationFn: (configData) => ApiService.updateBadgeConfig(eventId, configData),
    onSuccess: () => {
      queryClient.invalidateQueries(['badgeConfig', eventId]);
      showSuccess('Badge configuration saved successfully!');
      setIsEditing(false);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save badge configuration. Please try again.';
      showError(errorMessage);
      console.error('Save config error:', error);
    },
  });

  // Initialize configuration when fetched or use default
  useEffect(() => {
    if (config) {
      setBadgeConfig(config);
    } else if (config === null && !configLoading && !configError) {
      // No config exists, use default
      setBadgeConfig(defaultBadgeConfig);
      setIsEditing(true); // Start in editing mode for new configs
    }
  }, [config, configLoading, configError]);

  const handleFieldChange = (section, field, value) => {
    setBadgeConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Handle real-time config updates for preview
  const handleConfigChange = (section, field, value) => {
    if (!badgeConfig) return;
    
    const updatedConfig = { ...badgeConfig };
    
    if (section === 'front') {
      updatedConfig.front = { ...updatedConfig.front, [field]: value };
    } else if (section === 'back') {
      updatedConfig.back = { ...updatedConfig.back, [field]: value };
    } else if (section === 'global') {
      updatedConfig.globalSettings = { ...updatedConfig.globalSettings, [field]: value };
    }
    
    setBadgeConfig(updatedConfig);
  };

  // Handle field updates
  const handleFieldUpdate = (side, fieldIndex, fieldUpdates) => {
    if (!badgeConfig) return;
    
    const updatedConfig = { ...badgeConfig };
    const fields = side === 'front' ? updatedConfig.front.fields : updatedConfig.back.fields;
    
    fields[fieldIndex] = { ...fields[fieldIndex], ...fieldUpdates };
    
    if (side === 'front') {
      updatedConfig.front = { ...updatedConfig.front, fields };
    } else {
      updatedConfig.back = { ...updatedConfig.back, fields };
    }
    
    setBadgeConfig(updatedConfig);
  };

  // Add new field
  const handleAddField = (side) => {
    if (!badgeConfig) return;
    
    const updatedConfig = { ...badgeConfig };
    const newField = {
      key: `new_field_${Date.now()}`,
      label: 'New Field',
      visible: true,
      style: {
        fontSize: '14px',
        fontWeight: 'normal',
        color: '#1e293b',
        fontFamily: 'inherit',
        textAlign: 'left'
      },
      // Default positioning - will be auto-positioned if not set
      x: undefined,
      y: undefined
    };
    
    if (side === 'front') {
      updatedConfig.front = {
        ...updatedConfig.front,
        fields: [...(updatedConfig.front.fields || []), newField]
      };
    } else {
      updatedConfig.back = {
        ...updatedConfig.back,
        fields: [...(updatedConfig.back.fields || []), newField]
      };
    }
    
    setBadgeConfig(updatedConfig);
  };

  // Remove field
  const handleRemoveField = (side, fieldIndex) => {
    if (!badgeConfig) return;
    
    const updatedConfig = { ...badgeConfig };
    
    if (side === 'front') {
      const newFields = [...(updatedConfig.front.fields || [])];
      newFields.splice(fieldIndex, 1);
      updatedConfig.front = { ...updatedConfig.front, fields: newFields };
    } else {
      const newFields = [...(updatedConfig.back.fields || [])];
      newFields.splice(fieldIndex, 1);
      updatedConfig.back = { ...updatedConfig.back, fields: newFields };
    }
    
    setBadgeConfig(updatedConfig);
  };

  const handleFieldArrayChange = (section, field, value) => {
    setBadgeConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleGlobalSettingChange = (key, value) => {
    setBadgeConfig(prev => ({
      ...prev,
      globalSettings: {
        ...prev.globalSettings,
        [key]: value
      }
    }));
  };

  const handlePrintConfigChange = (key, value) => {
    setBadgeConfig(prev => ({
      ...prev,
      printConfig: {
        ...prev.printConfig,
        [key]: value
      }
    }));
  };

  const handleMarginChange = (side, value) => {
    setBadgeConfig(prev => ({
      ...prev,
      printConfig: {
        ...prev.printConfig,
        margin: {
          ...prev.printConfig?.margin,
          [side]: value
        }
      }
    }));
  };

  const handleSaveConfig = () => {
    saveConfigMutation.mutate(badgeConfig);
  };

  const handleCancelEdit = () => {
    setBadgeConfig(config);
    setIsEditing(false);
  };

  const handleFileUpload = async (file, assetType) => {
    try {
      const fileUrl = await ApiService.uploadBadgeAsset(eventId, assetType, file);
      showSuccess(`${assetType} uploaded successfully!`);
      return fileUrl;
    } catch (error) {
      showError(`Failed to upload ${assetType}. Please try again.`);
      throw error;
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading badge configuration...</p>
        </div>
      </div>
    );
  }

  if (configError) {
    // Safely extract error message, handling nested error objects
    const errorMessage = typeof configError === 'string' 
      ? configError 
      : configError?.response?.data?.message 
      || configError?.message 
      || (typeof configError.message === 'object' ? JSON.stringify(configError.message) : String(configError.message))
      || 'Unable to load badge configuration. Please check your connection and try again.';
    
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Badge Configuration</h2>
          <p className="text-gray-600 mb-4">
            {errorMessage}
          </p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!badgeConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p>No badge configuration found. Creating default configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Badge Configuration</h1>
        </div>
        <div className="flex space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit} disabled={saveConfigMutation.isLoading}>
                Cancel
              </Button>
              <Button onClick={handleSaveConfig} disabled={saveConfigMutation.isLoading}>
                <Save className="w-4 h-4 mr-2" />
                {saveConfigMutation.isLoading ? 'Saving...' : 'Save Configuration'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Configuration
            </Button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        <Button
          variant={activeTab === 'front' ? 'default' : 'outline'}
          onClick={() => setActiveTab('front')}
          className="flex items-center space-x-2"
        >
          <Type className="w-4 h-4" />
          <span>Front Side</span>
        </Button>
        <Button
          variant={activeTab === 'back' ? 'default' : 'outline'}
          onClick={() => setActiveTab('back')}
          className="flex items-center space-x-2"
        >
          <Type className="w-4 h-4" />
          <span>Back Side</span>
        </Button>
        <Button
          variant={activeTab === 'global' ? 'default' : 'outline'}
          onClick={() => setActiveTab('global')}
          className="flex items-center space-x-2"
        >
          <Settings className="w-4 h-4" />
          <span>Global Settings</span>
        </Button>
        <Button
          variant={activeTab === 'print' ? 'default' : 'outline'}
          onClick={() => setActiveTab('print')}
          className="flex items-center space-x-2"
        >
          <Printer className="w-4 h-4" />
          <span>Print Settings</span>
        </Button>
      </div>

      {/* Main Layout: Configuration + Live Preview */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 min-h-screen">
        {/* Configuration Panel */}
        <div className="space-y-6">
        {activeTab === 'front' && (
          <>
            {/* Front Side Design */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Palette className="w-5 h-5" />
                  <span>Front Side Design</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="frontBackgroundColor">Background Color</Label>
                  <Input
                    id="frontBackgroundColor"
                    type="color"
                    value={badgeConfig.front?.backgroundColor || '#ffffff'}
                    onChange={(e) => {
                      handleFieldChange('front', 'backgroundColor', e.target.value);
                      handleConfigChange('front', 'backgroundColor', e.target.value);
                    }}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                  />
                </div>
                <div>
                  <Label htmlFor="frontBackgroundImage">Background Image URL</Label>
                  <Input
                    id="frontBackgroundImage"
                    type="text"
                    value={badgeConfig.front?.backgroundImage || ''}
                    onChange={(e) => {
                      handleFieldChange('front', 'backgroundImage', e.target.value);
                      handleConfigChange('front', 'backgroundImage', e.target.value);
                    }}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                    placeholder="https://example.com/image.png"
                  />
                </div>
                <div>
                  <Label htmlFor="frontLogo">Logo URL</Label>
                  <Input
                    id="frontLogo"
                    type="text"
                    value={badgeConfig.front?.logo || ''}
                    onChange={(e) => {
                      handleFieldChange('front', 'logo', e.target.value);
                      handleConfigChange('front', 'logo', e.target.value);
                    }}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
                
                {/* Logo Position Controls */}
                {badgeConfig.front?.logo && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Logo X Position</Label>
                      <Input
                        type="number"
                        value={badgeConfig.front?.logoPosition?.x || 0}
                        onChange={(e) => {
                          const logoPosition = { ...badgeConfig.front?.logoPosition, x: parseInt(e.target.value) || 0 };
                          handleFieldChange('front', 'logoPosition', logoPosition);
                          handleConfigChange('front', 'logoPosition', logoPosition);
                        }}
                        disabled={!isEditing || updateConfigMutation.isLoading}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Logo Y Position</Label>
                      <Input
                        type="number"
                        value={badgeConfig.front?.logoPosition?.y || 0}
                        onChange={(e) => {
                          const logoPosition = { ...badgeConfig.front?.logoPosition, y: parseInt(e.target.value) || 0 };
                          handleFieldChange('front', 'logoPosition', logoPosition);
                          handleConfigChange('front', 'logoPosition', logoPosition);
                        }}
                        disabled={!isEditing || updateConfigMutation.isLoading}
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}
                
                {/* QR Code Position Controls */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">QR X Position</Label>
                    <Input
                      type="number"
                      value={badgeConfig.front?.qrCodePosition?.x || 10}
                      onChange={(e) => {
                        const qrPosition = { ...badgeConfig.front?.qrCodePosition, x: parseInt(e.target.value) || 10 };
                        handleFieldChange('front', 'qrCodePosition', qrPosition);
                        handleConfigChange('front', 'qrCodePosition', qrPosition);
                      }}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                      placeholder="10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">QR Y Position</Label>
                    <Input
                      type="number"
                      value={badgeConfig.front?.qrCodePosition?.y || 10}
                      onChange={(e) => {
                        const qrPosition = { ...badgeConfig.front?.qrCodePosition, y: parseInt(e.target.value) || 10 };
                        handleFieldChange('front', 'qrCodePosition', qrPosition);
                        handleConfigChange('front', 'qrCodePosition', qrPosition);
                      }}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                      placeholder="10"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs">QR Code Size</Label>
                  <Input
                    type="number"
                    value={badgeConfig.front?.qrCodePosition?.size || 80}
                    onChange={(e) => {
                      const qrPosition = { ...badgeConfig.front?.qrCodePosition, size: parseInt(e.target.value) || 80 };
                      handleFieldChange('front', 'qrCodePosition', qrPosition);
                      handleConfigChange('front', 'qrCodePosition', qrPosition);
                    }}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                    placeholder="80"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Front Side Fields */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Type className="w-5 h-5" />
                  <span>Front Side Fields</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {badgeConfig.front?.fields?.map((field, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Label className="font-medium">{field.label}</Label>
                        {isEditing && (
                          <Button
                            onClick={() => handleRemoveField('front', index)}
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <Switch
                        checked={field.visible}
                        onCheckedChange={(checked) => {
                          const newFields = [...badgeConfig.front.fields];
                          newFields[index] = { ...field, visible: checked };
                          handleFieldArrayChange('front', 'fields', newFields);
                          handleFieldUpdate('front', index, { visible: checked });
                        }}
                        disabled={!isEditing || updateConfigMutation.isLoading}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Field Label</Label>
                        <Input
                          value={field.label || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.front.fields];
                            newFields[index] = { ...field, label: e.target.value };
                            handleFieldArrayChange('front', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="Field Label"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Field Key</Label>
                        <Input
                          value={field.key || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.front.fields];
                            newFields[index] = { ...field, key: e.target.value };
                            handleFieldArrayChange('front', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="field_key"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Font Size</Label>
                        <Input
                          value={field.style?.fontSize || '14px'}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.front.fields];
                            newFields[index] = {
                              ...field,
                              style: { ...field.style, fontSize: e.target.value }
                            };
                            handleFieldArrayChange('front', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Color</Label>
                        <Input
                          type="color"
                          value={field.style?.color || '#1e293b'}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.front.fields];
                            newFields[index] = {
                              ...field,
                              style: { ...field.style, color: e.target.value }
                            };
                            handleFieldArrayChange('front', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                        />
                      </div>
                    </div>
                    
                    {/* Field Position Controls */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">X Position (px)</Label>
                        <Input
                          type="number"
                          value={field.x || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.front.fields];
                            newFields[index] = {
                              ...field,
                              x: e.target.value ? parseInt(e.target.value) : undefined
                            };
                            handleFieldArrayChange('front', 'fields', newFields);
                            // Update live preview
                            const updatedConfig = { ...badgeConfig };
                            updatedConfig.front = { ...updatedConfig.front, fields: newFields };
                            setBadgeConfig(updatedConfig);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="Auto"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Y Position (px)</Label>
                        <Input
                          type="number"
                          value={field.y || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.front.fields];
                            newFields[index] = {
                              ...field,
                              y: e.target.value ? parseInt(e.target.value) : undefined
                            };
                            handleFieldArrayChange('front', 'fields', newFields);
                            // Update live preview
                            const updatedConfig = { ...badgeConfig };
                            updatedConfig.front = { ...updatedConfig.front, fields: newFields };
                            setBadgeConfig(updatedConfig);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="Auto"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Field Button */}
                {isEditing && (
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={() => handleAddField('front')} 
                      variant="outline" 
                      size="sm"
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Front Field
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'back' && (
          <>
            {/* Back Side Design */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Palette className="w-5 h-5" />
                  <span>Back Side Design</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="backBackgroundColor">Background Color</Label>
                  <Input
                    id="backBackgroundColor"
                    type="color"
                    value={badgeConfig.back?.backgroundColor || '#f8f9fa'}
                    onChange={(e) => handleFieldChange('back', 'backgroundColor', e.target.value)}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                  />
                </div>
                <div>
                  <Label htmlFor="backBackgroundImage">Background Image URL</Label>
                  <Input
                    id="backBackgroundImage"
                    type="text"
                    value={badgeConfig.back?.backgroundImage || ''}
                    onChange={(e) => handleFieldChange('back', 'backgroundImage', e.target.value)}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                    placeholder="https://example.com/image.png"
                  />
                </div>
                
                {/* QR Code Position Controls for Back Side */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">QR X Position</Label>
                    <Input
                      type="number"
                      value={badgeConfig.back?.qrCodePosition?.x || 10}
                      onChange={(e) => {
                        const qrPosition = { ...badgeConfig.back?.qrCodePosition, x: parseInt(e.target.value) || 10 };
                        handleFieldChange('back', 'qrCodePosition', qrPosition);
                        handleConfigChange('back', 'qrCodePosition', qrPosition);
                      }}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                      placeholder="10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">QR Y Position</Label>
                    <Input
                      type="number"
                      value={badgeConfig.back?.qrCodePosition?.y || 10}
                      onChange={(e) => {
                        const qrPosition = { ...badgeConfig.back?.qrCodePosition, y: parseInt(e.target.value) || 10 };
                        handleFieldChange('back', 'qrCodePosition', qrPosition);
                        handleConfigChange('back', 'qrCodePosition', qrPosition);
                      }}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                      placeholder="10"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs">QR Code Size</Label>
                  <Input
                    type="number"
                    value={badgeConfig.back?.qrCodePosition?.size || 80}
                    onChange={(e) => {
                      const qrPosition = { ...badgeConfig.back?.qrCodePosition, size: parseInt(e.target.value) || 80 };
                      handleFieldChange('back', 'qrCodePosition', qrPosition);
                      handleConfigChange('back', 'qrCodePosition', qrPosition);
                    }}
                    disabled={!isEditing || updateConfigMutation.isLoading}
                    placeholder="80"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Back Side Fields */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Type className="w-5 h-5" />
                  <span>Back Side Fields</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {badgeConfig.back?.fields?.map((field, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Label className="font-medium">{field.label}</Label>
                        {isEditing && (
                          <Button
                            onClick={() => handleRemoveField('back', index)}
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <Switch
                        checked={field.visible}
                        onCheckedChange={(checked) => {
                          const newFields = [...badgeConfig.back.fields];
                          newFields[index] = { ...field, visible: checked };
                          handleFieldArrayChange('back', 'fields', newFields);
                        }}
                        disabled={!isEditing || updateConfigMutation.isLoading}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Field Label</Label>
                        <Input
                          value={field.label || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.back.fields];
                            newFields[index] = { ...field, label: e.target.value };
                            handleFieldArrayChange('back', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="Field Label"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Field Key</Label>
                        <Input
                          value={field.key || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.back.fields];
                            newFields[index] = { ...field, key: e.target.value };
                            handleFieldArrayChange('back', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="field_key"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Font Size</Label>
                        <Input
                          value={field.style?.fontSize || '14px'}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.back.fields];
                            newFields[index] = {
                              ...field,
                              style: { ...field.style, fontSize: e.target.value }
                            };
                            handleFieldArrayChange('back', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Color</Label>
                        <Input
                          type="color"
                          value={field.style?.color || '#1e293b'}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.back.fields];
                            newFields[index] = {
                              ...field,
                              style: { ...field.style, color: e.target.value }
                            };
                            handleFieldArrayChange('back', 'fields', newFields);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                        />
                      </div>
                    </div>
                    
                    {/* Field Position Controls */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">X Position (px)</Label>
                        <Input
                          type="number"
                          value={field.x || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.back.fields];
                            newFields[index] = {
                              ...field,
                              x: e.target.value ? parseInt(e.target.value) : undefined
                            };
                            handleFieldArrayChange('back', 'fields', newFields);
                            // Update live preview
                            const updatedConfig = { ...badgeConfig };
                            updatedConfig.back = { ...updatedConfig.back, fields: newFields };
                            setBadgeConfig(updatedConfig);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="Auto"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Y Position (px)</Label>
                        <Input
                          type="number"
                          value={field.y || ''}
                          onChange={(e) => {
                            const newFields = [...badgeConfig.back.fields];
                            newFields[index] = {
                              ...field,
                              y: e.target.value ? parseInt(e.target.value) : undefined
                            };
                            handleFieldArrayChange('back', 'fields', newFields);
                            // Update live preview
                            const updatedConfig = { ...badgeConfig };
                            updatedConfig.back = { ...updatedConfig.back, fields: newFields };
                            setBadgeConfig(updatedConfig);
                          }}
                          disabled={!isEditing || updateConfigMutation.isLoading}
                          placeholder="Auto"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Field Button */}
                {isEditing && (
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={() => handleAddField('back')} 
                      variant="outline" 
                      size="sm"
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Back Field
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'global' && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Global Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="badgeWidth">Badge Width (px)</Label>
                <Input
                  id="badgeWidth"
                  type="number"
                  value={badgeConfig.globalSettings?.badgeWidth || 300}
                  onChange={(e) => handleGlobalSettingChange('badgeWidth', parseInt(e.target.value))}
                  disabled={!isEditing || updateConfigMutation.isLoading}
                />
              </div>
              <div>
                <Label htmlFor="badgeHeight">Badge Height (px)</Label>
                <Input
                  id="badgeHeight"
                  type="number"
                  value={badgeConfig.globalSettings?.badgeHeight || 200}
                  onChange={(e) => handleGlobalSettingChange('badgeHeight', parseInt(e.target.value))}
                  disabled={!isEditing || updateConfigMutation.isLoading}
                />
              </div>
              <div>
                <Label htmlFor="borderRadius">Border Radius (px)</Label>
                <Input
                  id="borderRadius"
                  type="number"
                  value={badgeConfig.globalSettings?.borderRadius || 8}
                  onChange={(e) => handleGlobalSettingChange('borderRadius', parseInt(e.target.value))}
                  disabled={!isEditing || updateConfigMutation.isLoading}
                />
              </div>
              <div>
                <Label htmlFor="borderWidth">Border Width (px)</Label>
                <Input
                  id="borderWidth"
                  type="number"
                  value={badgeConfig.globalSettings?.borderWidth || 1}
                  onChange={(e) => handleGlobalSettingChange('borderWidth', parseInt(e.target.value))}
                  disabled={!isEditing || updateConfigMutation.isLoading}
                />
              </div>
              <div>
                <Label htmlFor="borderColor">Border Color</Label>
                <Input
                  id="borderColor"
                  type="color"
                  value={badgeConfig.globalSettings?.borderColor || '#e5e7eb'}
                  onChange={(e) => handleGlobalSettingChange('borderColor', e.target.value)}
                  disabled={!isEditing || updateConfigMutation.isLoading}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'print' && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Printer className="w-5 h-5" />
                <span>Print Configuration</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Paper Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Paper Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="paperSize">Paper Size</Label>
                    <Select
                      value={badgeConfig.printConfig?.paperSize || 'A4'}
                      onValueChange={(value) => handlePrintConfigChange('paperSize', value)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select paper size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
                        <SelectItem value="A3">A3 (297 × 420 mm)</SelectItem>
                        <SelectItem value="A5">A5 (148 × 210 mm)</SelectItem>
                        <SelectItem value="Letter">Letter (8.5 × 11 in)</SelectItem>
                        <SelectItem value="Legal">Legal (8.5 × 14 in)</SelectItem>
                        <SelectItem value="Tabloid">Tabloid (11 × 17 in)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="orientation">Orientation</Label>
                    <Select
                      value={badgeConfig.printConfig?.orientation || 'portrait'}
                      onValueChange={(value) => handlePrintConfigChange('orientation', value)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select orientation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portrait">Portrait</SelectItem>
                        <SelectItem value="landscape">Landscape</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Margins */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Margins (mm)</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="marginTop">Top</Label>
                    <Input
                      id="marginTop"
                      type="number"
                      value={badgeConfig.printConfig?.margin?.top || 20}
                      onChange={(e) => handleMarginChange('top', parseInt(e.target.value) || 20)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="marginRight">Right</Label>
                    <Input
                      id="marginRight"
                      type="number"
                      value={badgeConfig.printConfig?.margin?.right || 20}
                      onChange={(e) => handleMarginChange('right', parseInt(e.target.value) || 20)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="marginBottom">Bottom</Label>
                    <Input
                      id="marginBottom"
                      type="number"
                      value={badgeConfig.printConfig?.margin?.bottom || 20}
                      onChange={(e) => handleMarginChange('bottom', parseInt(e.target.value) || 20)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="marginLeft">Left</Label>
                    <Input
                      id="marginLeft"
                      type="number"
                      value={badgeConfig.printConfig?.margin?.left || 20}
                      onChange={(e) => handleMarginChange('left', parseInt(e.target.value) || 20)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Print Quality & Layout */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Quality & Layout</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="scale">Scale (%)</Label>
                    <Input
                      id="scale"
                      type="number"
                      min="10"
                      max="200"
                      step="10"
                      value={Math.round((badgeConfig.printConfig?.scale || 1.0) * 100)}
                      onChange={(e) => handlePrintConfigChange('scale', parseFloat(e.target.value) / 100 || 1.0)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="quality">Print Quality</Label>
                    <Select
                      value={badgeConfig.printConfig?.quality || 'high'}
                      onValueChange={(value) => handlePrintConfigChange('quality', value)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select quality" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft (Fast)</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High Quality</SelectItem>
                        <SelectItem value="best">Best Quality</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="colorMode">Color Mode</Label>
                    <Select
                      value={badgeConfig.printConfig?.colorMode || 'color'}
                      onValueChange={(value) => handlePrintConfigChange('colorMode', value)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select color mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="color">Color</SelectItem>
                        <SelectItem value="grayscale">Grayscale</SelectItem>
                        <SelectItem value="blackandwhite">Black & White</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="badgesPerPage">Badges Per Page</Label>
                    <Input
                      id="badgesPerPage"
                      type="number"
                      min="1"
                      max="16"
                      value={badgeConfig.printConfig?.badgesPerPage || 1}
                      onChange={(e) => handlePrintConfigChange('badgesPerPage', parseInt(e.target.value) || 1)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Print Options */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Print Options</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Double-sided Printing</Label>
                      <p className="text-sm text-gray-500">Print on both sides of the paper</p>
                    </div>
                    <Switch
                      checked={badgeConfig.printConfig?.doubleSided || false}
                      onCheckedChange={(checked) => handlePrintConfigChange('doubleSided', checked)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Cut Lines</Label>
                      <p className="text-sm text-gray-500">Add cut lines for easier badge cutting</p>
                    </div>
                    <Switch
                      checked={badgeConfig.printConfig?.cutLines || false}
                      onCheckedChange={(checked) => handlePrintConfigChange('cutLines', checked)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Print Backgrounds</Label>
                      <p className="text-sm text-gray-500">Include background colors and images in print</p>
                    </div>
                    <Switch
                      checked={badgeConfig.printConfig?.backgroundPrint !== false}
                      onCheckedChange={(checked) => handlePrintConfigChange('backgroundPrint', checked)}
                      disabled={!isEditing || updateConfigMutation.isLoading}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </div>


        {/* Live Preview Panel - Sticky */}
        <div className="xl:sticky xl:top-6 xl:self-start space-y-6 xl:max-h-screen xl:overflow-y-auto">
          <LivePreviewPanel 
            eventId={eventId}
            badgeConfig={debouncedBadgeConfig}
            isLoading={configLoading || saveConfigMutation.isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default BadgeConfigurationPage;